import { useState, useEffect } from "react";
import { FaPrint, FaEdit } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { HiXMark } from "react-icons/hi2";
import { format } from "date-fns";
import axios from "axios";
import { PrintLayout } from "../../PrintLayout";
import { useAuth } from "../../../utils/AuthContext";

// 2. Define API Config
const API_KEY =
  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const BASE_URL = "https://api.muktihospital.com/api";

const OutdoorViewBillModal = ({
  isOpen,
  onClose,
  bill: initialBill,
  onEdit,
  onDelete,
}) => {
  const [bill, setBill] = useState(initialBill);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();
  

  useEffect(() => {
    const fetchFullBillDetails = async () => {
      if (initialBill?.id) {
        setIsLoading(true);
        try {
          const res = await axios.get(
            `${BASE_URL}/outdoor-bill/${initialBill.id}`,
            {
              headers: { "x-api-key": API_KEY },
            }
          );
          setBill(res.data.bill);
        } catch (error) {
          console.error("Error fetching bill details:", error);
          setBill(initialBill);
        } finally {
          setIsLoading(false);
        }
      }
    };

    if (isOpen) {
      fetchFullBillDetails();
    }
  }, [isOpen, initialBill]);

  if (!isOpen || !bill) return null;

  const {
    id,
    billNumber,
    createdAt,
    billDate,
    admissionDate,
    patientName,
    phoneNumber,
    patient,
    refer,
    consultantName,
    items = [],
    billAmount = 0,
    discountPc = 0,
    discount = 0,
    serviceChargePc = 0,
    serviceCharge = 0,
    netAmount = 0,
    paidAmount = 0,
    payableAmount = 0,
    userName,
  } = bill;
  const finalPatientName = patientName || patient?.name || "N/A";
  const finalPhoneNumber = phoneNumber || patient?.phoneNumber || "N/A";

  const serviceList =
    items.length > 0
      ? items.map((item, index) => ({
          id: item.id || `service-${index}`,
          name: item.serviceName || "N/A",
          quantity: item.totalUnit || 0,
          price: item.unitPrice || 0,
          amount: item.amount || 0,
        }))
      : [];

  const subTotal = serviceList.reduce(
    (sum, service) => sum + (parseFloat(service.amount) || 0),
    0
  );
  const dueAmount = (
    payableAmount > 0 ? payableAmount : netAmount - paidAmount
  ).toFixed(2);

  // --- HANDLERS ---
  const handlePrintBill = () => {
    const elementsToHide = document.querySelectorAll(".print-hidden");
    elementsToHide.forEach((el) => (el.style.display = "none"));

    const printLayout = document.querySelector(".print-layout");
    if (printLayout) {
      printLayout.style.display = "block";
    }
    window.print();
    setTimeout(() => {
      elementsToHide.forEach((el) => (el.style.display = ""));
      if (printLayout) {
        printLayout.style.display = "none";
      }
    }, 500);
  };

  const billInfoContent = (
    <>
      <div>
        <p>
          <strong>Bill No:</strong> {billNumber || "N/A"}
        </p>
        <p><strong>Patient Name:</strong> {finalPatientName}</p>
        <p><strong>Phone:</strong> {finalPhoneNumber}</p>
      </div>
      <div>
        <p>
          <strong>Admission Date:</strong>{" "}
          {admissionDate
            ? format(new Date(admissionDate), "dd/MM/yyyy")
            : "N/A"}
        </p>
        <p>
          <strong>Billing Date:</strong>{" "}
          {billDate
            ? format(new Date(billDate), "dd/MM/yyyy")
            : format(new Date(createdAt), "dd/MM/yyyy")}
        </p>
        <p>
          <strong>Consultant:</strong> {consultantName || "N/A"}
        </p>
      </div>
    </>
  );

  const tableContent = (
    <>
      <thead>
        <tr>
          <th>Sl No</th>
          <th>Service Name</th>
          <th>Quantity</th>
          <th>Unit Price (৳)</th>
          <th>Amount (৳)</th>
        </tr>
      </thead>
      <tbody>
        {serviceList.length > 0 ? (
          serviceList.map((service, index) => (
            <tr key={service.id}>
              <td>{index + 1}</td>
              <td>{service.name}</td>
              <td>{service.quantity}</td>
              <td>৳ {parseFloat(service.price).toFixed(2)}</td>
              <td>৳ {parseFloat(service.amount).toFixed(2)}</td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan="5" style={{ textAlign: "center" }}>
              No services available
            </td>
          </tr>
        )}
      </tbody>
    </>
  );

  const summaryContent = (
    <>
      {serviceCharge > 0 && (
        <div>
          <span>Total Amount</span>
          <span>
            ৳ {(parseFloat(billAmount) + parseFloat(serviceCharge)).toFixed(2)}
          </span>
        </div>
      )}
      <div>
        <span>Discount {discountPc > 0 ? `(${discountPc}%)` : ""}</span>
        <span>৳ {parseFloat(discount || 0).toFixed(2)}</span>
      </div>
      <div>
        <span>Net Total</span>
        <span>৳ {parseFloat(netAmount || 0).toFixed(2)}</span>
      </div>
      <div>
        <span>Paid Amount</span>
        <span>৳ {parseFloat(paidAmount || 0).toFixed(2)}</span>
      </div>
      <div>
        <span>Due Amount</span>
        <span>৳ {dueAmount}</span>
      </div>
    </>
  );

  return (
    <>
      {/* Modal Content */}
      <div className="fixed inset-0 z-40 flex items-start sm:items-center justify-center bg-black bg-opacity-40 px-4 sm:px-0 overflow-y-auto print-hidden">
        <div className="bg-white shadow-lg w-full my-4 sm:my-0 max-h-[100vh] h-full overflow-y-auto rounded-md">
          {/* Header */}
          <div className="flex items-center justify-between gap-3 flex-wrap bg-gray-200 border-b border-dashed border-gray-300 p-4 print-hidden">
            <div className="flex items-center gap-3">
              <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                Outdoor Bill Details
              </h2>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={handlePrintBill}
                className="size-7 rounded-full bg-white text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                title="Print"
              >
                <FaPrint />
              </button>
              <button
                onClick={() => {
                  onEdit(bill);
                  onClose();
                }}
                className="size-7 rounded-full bg-white text-M-Green-color text-sm inline-flex items-center justify-center hover:bg-M-Green-color hover:text-white transition-all duration-300"
                title="Edit"
              >
                <FaEdit />
              </button>
              <button
                onClick={() => {
                  onDelete(id);
                  onClose();
                }}
                className="size-7 rounded-full bg-white text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                title="Delete"
              >
                <RiDeleteBin6Line />
              </button>
              <button
                onClick={onClose}
                className="size-7 rounded-full bg-white text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                title="Close"
              >
                <HiXMark />
              </button>
            </div>
          </div>

          <div className="px-6 py-6 print-hidden">
            {/* Bill Info */}
            <div className="mt-2 sm:flex justify-between items-start mb-6">
              <div className="space-y-1">
                <p className="text-black mb-1 text-[15px]">
                  <span className="w-[120px] inline-block">
                    Bill No
                  </span>:
                  <span className="text-gray-600 pl-1">
                    {billNumber || "N/A"}
                  </span>
                </p>
                <p className="text-black mb-1 text-[15px]">
                  <span className="w-[120px] inline-block">
                    Patient Name
                  </span>:
                  <span className="text-gray-600 pl-1">
                    {finalPatientName || "N/A"}
                  </span>
                </p>
                <p className="text-black mb-1 text-[15px]">
                  <span className="w-[120px] inline-block ">
                    Phone
                  </span>:
                  <span className="text-gray-600 pl-1">
                    {finalPhoneNumber || "N/A"}
                  </span>
                </p>
                <p className="text-black mb-1 text-[15px]">
                  <span className="w-[120px] inline-block ">
                    Consultant
                  </span>:
                  <span className="text-gray-600 pl-1">
                    {consultantName || "N/A"}
                  </span>
                </p>
              </div>
              <div>
                <p className="text-black mb-1 text-[15px]">
                  <span className="w-[120px] inline-block ">
                    Billing Date
                  </span>:
                  <span className="text-gray-600 pl-1">
                    {billDate
                      ? format(new Date(billDate), "MM/dd/yyyy")
                      : format(new Date(createdAt), "MM/dd/yyyy")}
                  </span>
                </p>
                <p className="text-black mb-1 text-[15px]">
                  <span className="w-[120px] inline-block ">
                    Admission Date
                  </span>:
                  <span className="text-gray-600 pl-1">
                    {admissionDate
                      ? format(new Date(admissionDate), "MM/dd/yyyy")
                      : "N/A"}
                  </span>
                </p>
                <p className="text-black mb-1 text-[15px]">
                  <span className="w-[120px] inline-block ">
                    Refer
                  </span>:
                  <span className="text-gray-600 pl-1">{refer || "N/A"}</span>
                </p>
                <p className="text-black mb-1 text-[15px]">
                  <span className="w-[120px] inline-block ">
                    Submitted By
                  </span>:
                  <span className="text-gray-600 pl-1">
                    {user?.name || userName || "Unknown"}
                  </span>
                </p>
              </div>
            </div>

            {/* Services Table */}
            {serviceList.length > 0 ? (
              <div className="mt-4 sm:mt-6">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-xs sm:text-sm">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="p-2 sm:p-3 border font-semibold text-left">
                          Service Name
                        </th>
                        <th className="p-2 sm:p-3 border font-semibold text-center">
                          Quantity
                        </th>
                        <th className="p-2 sm:p-3 border font-semibold text-right">
                          Unit Price (৳)
                        </th>
                        <th className="p-2 sm:p-3 border font-semibold text-right">
                          Amount (৳)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {serviceList.map((service, index) => (
                        <tr key={service.id}>
                          <td className="p-2 sm:p-3 border text-left">
                            {service.name}
                          </td>
                          <td className="p-2 sm:p-3 border text-center">
                            {service.quantity}
                          </td>
                          <td className="p-2 sm:p-3 border text-right">
                            {parseFloat(service.price).toFixed(2)}
                          </td>
                          <td className="p-2 sm:p-3 border text-right">
                            {parseFloat(service.amount).toFixed(2)}
                          </td>
                        </tr>
                      ))}

                      {/* Footer Rows (Calculations) */}
                      <tr>
                        <td
                          className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3 pt-4"
                          colSpan="3"
                        >
                          <p>
                            <strong>Sub Total (৳) :</strong>
                          </p>
                        </td>
                        <td className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3 pt-4">
                          <p>{subTotal.toFixed(2)}</p>
                        </td>
                      </tr>
                      {serviceCharge > 0 && (
                        <tr>
                          <td
                            className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3"
                            colSpan="3"
                          >
                            <p>
                              <strong>
                                Service Charge ({serviceChargePc}%) :
                              </strong>
                            </p>
                          </td>
                          <td className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3">
                            <p>{parseFloat(serviceCharge).toFixed(2)}</p>
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td
                          className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3"
                          colSpan="3"
                        >
                          <p>
                            <strong>Discount (৳) :</strong>
                          </p>
                        </td>
                        <td className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3">
                          <p>{parseFloat(discount || 0).toFixed(2)}</p>
                        </td>
                      </tr>
                      <tr>
                        <td
                          className="text-end capitalize font-inter font-normal text-sm text-gray-800"
                          colSpan="3"
                        >
                          <strong className="py-1 px-3 text-sm">
                            Net Total (৳) :
                          </strong>
                        </td>
                        <td className="text-end capitalize font-inter font-normal text-sm text-gray-800">
                          <strong className="py-1 px-3 border-t border-gray-400/20 block">
                            {parseFloat(netAmount || 0).toFixed(2)}
                          </strong>
                        </td>
                      </tr>
                      <tr>
                        <td
                          className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3"
                          colSpan="3"
                        >
                          <p>
                            <strong>Paid Amount (৳) :</strong>
                          </p>
                        </td>
                        <td className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3">
                          <p>{parseFloat(paidAmount || 0).toFixed(2)}</p>
                        </td>
                      </tr>
                      <tr>
                        <td
                          className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3"
                          colSpan="3"
                        >
                          <p>
                            <strong>Due Amount (৳) :</strong>
                          </p>
                        </td>
                        <td className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3">
                          <p>{dueAmount}</p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-gray-500">
                No Services Found
              </div>
            )}
          </div>
        </div>
      </div>

      <PrintLayout
        className="print-layout"
        title="Outdoor Patient Bill"
        billInfo={billInfoContent}
        table={tableContent}
        summary={summaryContent}
      />
    </>
  );
};

export default OutdoorViewBillModal;
